/**
 * What waits on a coach, as one rule: NOVA Home's "Needs you" card and the
 * weekly digest email both build their rows here. Pure and universal.
 *
 * Aliases: needs you, waiting for you, digest rows
 */

import type { CoachAssetView } from "~/lib/functions/nova-assets";
import type { AssetDestination } from "~/lib/shared/nova/assets/destination";
import {
  assetDestination,
  assetDestinationPath,
} from "~/lib/shared/nova/assets/destination";

/** Someone who wrote first and is still waiting for her reply. */
export interface WaitingPerson {
  readonly name: string;
  /** Whole days since they wrote; 0 is today. */
  readonly days: number;
}

export type NeedsYouRow =
  | {
      readonly kind: "enquiries";
      readonly count: number;
      /** Longest-waiting first. */
      readonly people: readonly WaitingPerson[];
      /** The thread to open when exactly one person waits. */
      readonly conversationId: string | null;
    }
  | { readonly kind: "invoices"; readonly count: number }
  | {
      readonly kind: "review";
      readonly asset: CoachAssetView;
      readonly destination: AssetDestination;
    };

const DAY_MS = 24 * 60 * 60 * 1000;

/** What the messages read says about the people waiting for her reply. */
export interface WaitingEnquiriesRead {
  readonly total: number;
  readonly oldest: readonly {
    readonly conversationId: string;
    readonly senderName: string;
    readonly startedAt: Date;
  }[];
}

/**
 * What waits on her, from reads that already exist: people waiting for her
 * reply, invoices open past their due date, and NOVA versions waiting for her
 * review. A source that failed to load is null and leaves its row out rather
 * than claiming nothing waits.
 */
export function needsYouRows(input: {
  readonly waitingEnquiries: WaitingEnquiriesRead | null;
  readonly overdueInvoices: number | null;
  readonly assets: readonly CoachAssetView[];
  readonly now: Date;
}): NeedsYouRow[] {
  const rows: NeedsYouRow[] = [];
  const waiting = input.waitingEnquiries;
  if (waiting !== null && waiting.total > 0) {
    rows.push({
      kind: "enquiries",
      count: waiting.total,
      people: waiting.oldest.map((enquiry) => ({
        name: enquiry.senderName,
        days: Math.max(
          0,
          Math.floor(
            (input.now.getTime() - enquiry.startedAt.getTime()) / DAY_MS,
          ),
        ),
      })),
      conversationId:
        waiting.total === 1
          ? (waiting.oldest[0]?.conversationId ?? null)
          : null,
    });
  }
  if (input.overdueInvoices !== null && input.overdueInvoices > 0) {
    rows.push({ kind: "invoices", count: input.overdueInvoices });
  }
  for (const asset of input.assets) {
    if (asset.status !== "needs_review") continue;
    const destination = assetDestination(asset);
    if (destination) rows.push({ kind: "review", asset, destination });
  }
  return rows;
}

function waitedText(days: number): string {
  if (days === 0) return "today";
  return days === 1 ? "1 day" : `${days} days`;
}

/** A row's label and line, in the words the row is read in. */
export function needsYouText(row: NeedsYouRow): {
  readonly tag: string;
  readonly body: string;
  /** A second, quieter line under the body. */
  readonly detail?: string;
} {
  switch (row.kind) {
    case "enquiries":
      return {
        tag: "Enquiries",
        body:
          row.count === 1
            ? "1 person is waiting for your reply"
            : `${row.count} people are waiting for your reply`,
        detail: row.people
          .map((person) => `${person.name}, ${waitedText(person.days)}`)
          .join(" · "),
      };
    case "invoices":
      return {
        tag: "Invoices",
        body:
          row.count === 1
            ? "1 invoice is overdue"
            : `${row.count} invoices are overdue`,
      };
    case "review":
      return {
        tag: "NOVA",
        body: `${row.asset.label}: waiting for your review`,
      };
    default: {
      const exhaustive: never = row;
      throw new Error(`Unknown needs-you row: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** A key for a row in a list: one row per kind, one per asset in review. */
export function needsYouRowKey(row: NeedsYouRow): string {
  return row.kind === "review" ? `review:${row.asset.key}` : row.kind;
}

/** Where a row leads, as a site path: the page that resolves it. */
export function needsYouPath(row: NeedsYouRow): string {
  switch (row.kind) {
    case "enquiries":
      return row.conversationId
        ? `/messages/${row.conversationId}`
        : "/messages";
    case "invoices":
      return "/my-coaching/clients/invoices";
    case "review":
      return assetDestinationPath(row.destination);
    default: {
      const exhaustive: never = row;
      throw new Error(`Unknown needs-you row: ${JSON.stringify(exhaustive)}`);
    }
  }
}
